import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, model, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import {
  PropertyApplicationCreationDto,
  PropertyApplicationDto,
  PropertyApplicationStatus,
  PropertyDto,
} from '@workspace/data-types';
import { EtxePropertyApplicationsOwnerComparisonComponent } from '../../../property-application/components/owner-comparison/property-applications-owner-comparison.component';
import { EtxePropertyApplicationOwnerDrawerComponent } from '../../../property-application/components/owner-drawer/property-application-owner-drawer.component';
import { PropertyOwnerDetailsSkeletonComponent } from '../details/property-owner-details-skeleton/property-owner-details-skeleton.component';
import { PropertyCandidacyCardComponent } from '../property-application/candidacy-card/property-candidacy-card.component';
import { EtxePropertyViewComponent } from '../property-view/property-view.component';

export type PropertyOwnerDetailsTab = 'applications' | 'property' | 'agent-preview';

@Component({
  selector: 'etxe-property-owner-details-view',
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    EtxePropertyViewComponent,
    PropertyOwnerDetailsSkeletonComponent,
    EtxePropertyApplicationsOwnerComparisonComponent,
    EtxePropertyApplicationOwnerDrawerComponent,
    PropertyCandidacyCardComponent,
  ],
  templateUrl: './property-owner-details-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EtxePropertyOwnerDetailsViewComponent {
  readonly property = input<PropertyDto | null>(null);
  readonly applications = input<PropertyApplicationDto[]>([]);
  readonly isLoading = input<boolean>(false);
  readonly isLoadingApplications = input<boolean>(false);
  readonly error = input<string | null>(null);
  readonly demoMode = input<boolean>(false);

  readonly activeTab = model<PropertyOwnerDetailsTab>('property');

  readonly tabChanged = output<PropertyOwnerDetailsTab>();
  readonly refreshApplications = output<void>();
  readonly acceptApplication = output<PropertyApplicationDto>();
  readonly rejectApplication = output<PropertyApplicationDto>();
  readonly demoAgentApply = output<PropertyApplicationCreationDto>();
  readonly propertyUpdated = output<PropertyDto>();

  readonly selectedApplication = signal<PropertyApplicationDto | null>(null);
  readonly isDrawerOpen = signal<boolean>(false);

  readonly activeApplicationsCount = computed<number>(() => {
    return this.applications().filter(
      (app) =>
        app.status !== PropertyApplicationStatus.CANCELLED &&
        app.status !== PropertyApplicationStatus.REJECTED &&
        app.status !== PropertyApplicationStatus.EXPIRED,
    ).length;
  });

  readonly cancelledApplicationsCount = computed<number>(() => {
    return this.applications().filter(
      (app) =>
        app.status === PropertyApplicationStatus.CANCELLED ||
        app.status === PropertyApplicationStatus.REJECTED ||
        app.status === PropertyApplicationStatus.EXPIRED,
    ).length;
  });

  readonly coverPhotoUrl = computed<string | null>(() => {
    const prop = this.property();
    if (!prop) return null;
    return prop.media?.photos?.[0]?.url ?? null;
  });

  readonly agentPreviewProperty = computed<PropertyDto | null>(() => {
    const prop = this.property();
    if (!prop) return null;
    return {
      ...prop,
      media: prop.media
        ? {
            ...prop.media,
            photos: (prop.media.photos || []).filter((p) => !p.isHiddenBeforeMandate),
          }
        : prop.media,
    };
  });

  setTab(tab: PropertyOwnerDetailsTab): void {
    this.activeTab.set(tab);
    this.tabChanged.emit(tab);
  }

  openDrawer(app: PropertyApplicationDto): void {
    this.selectedApplication.set(app);
    this.isDrawerOpen.set(true);
  }

  closeDrawer(): void {
    this.isDrawerOpen.set(false);
  }

  onAcceptApplication(app: PropertyApplicationDto): void {
    this.acceptApplication.emit(app);
  }

  onRejectApplication(app: PropertyApplicationDto): void {
    this.rejectApplication.emit(app);
    this.closeDrawer();
  }
}
